import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';

// Filter data from filtros.json
const filtersData = {
  "Dados": [
    {
      "Descricao": "POTÊNCIA DO GERADOR (KWP)",
      "Id": 3,
      "Valor_Max": 3000,
      "Valor_Min": 0
    },
    {
      "Descricao": "TENSÃO SAIDA DO INVERSOR",
      "Id": 5,
      "Valores": [
        {
          "Descricao": "120V",
          "Id": 138
        },
        {
          "Descricao": "220V",
          "Id": 126
        },
        {
          "Descricao": "380V",
          "Id": 118
        }
      ]
    },
    {
      "Descricao": "INVERSOR SOLAR",
      "Id": 6,
      "Valores": [
        {
          "Descricao": "DEYE",
          "Id": 540
        },
        {
          "Descricao": "GROWATT",
          "Id": 245
        },
        {
          "Descricao": "NEP",
          "Id": 594
        }
      ]
    },
    {
      "Descricao": "PAINEL SOLAR",
      "Id": 8,
      "Valores": [
        {
          "Descricao": "DAH 585W BIF N-TYPE",
          "Id": 622
        },
        {
          "Descricao": "DAH 610W BIF N-TYPE",
          "Id": 623
        },
        {
          "Descricao": "JA MONO 550W",
          "Id": 553
        },
        {
          "Descricao": "JINKO MONO 470W NEO",
          "Id": 555
        },
        {
          "Descricao": "JINKO MONO 475W NEO",
          "Id": 558
        },
        {
          "Descricao": "JINKO MONO 555W",
          "Id": 596
        },
        {
          "Descricao": "OSDA 585W BIF N-TYPE",
          "Id": 624
        }
      ]
    },
    {
      "Descricao": "ESTRUTURA",
      "Id": 7,
      "Valores": [
        {
          "Descricao": "LAJE TRIANGULO",
          "Id": 140
        },
        {
          "Descricao": "PARAFUSO ESTRUTURAL FERRO",
          "Id": 156
        },
        {
          "Descricao": "PARAFUSO ESTRUTURAL MADEIRA",
          "Id": 141
        },
        {
          "Descricao": "SEM ESTRUTURA",
          "Id": 132
        },
        {
          "Descricao": "SOLO",
          "Id": 133
        },
        {
          "Descricao": "TELHA COLONIAL GANCHO",
          "Id": 131
        },
        {
          "Descricao": "TELHA METALICA PERFIL 55CM",
          "Id": 143
        },
        {
          "Descricao": "TELHA ONDULADA",
          "Id": 120
        }
      ]
    },
    {
      "Descricao": "BATERIA",
      "Id": 13,
      "Valores": [
        {
          "Descricao": "SEM BATERIA",
          "Id": 350
        },
        {
          "Descricao": "UNIPOWER LITIO",
          "Id": 593
        }
      ]
    },
    {
      "Descricao": "BOMBA DA AGUA SOLAR",
      "Id": 208,
      "Valores": [
        {
          "Descricao": "GRUNDFOS",
          "Id": 536
        }
      ]
    },
    {
      "Descricao": "TIPO PRODUTO",
      "Id": 1,
      "Valores": [
        {
          "Descricao": "GERADOR",
          "Id": 23
        }
      ]
    }
  ]
};

interface Product {
  Codigo: number;
  Nome: string;
  Potencia: number;
  Preco: number;
  Estoque: number;
  marcaInversor?: string;
  modeloInversor?: string;
  nFases?: number;
  tensao?: string;
  nMPPT?: number;
  quantidadePlacas?: number;
  potenciaPlaca?: number;
  marcaPlaca?: string;
  modeloPlaca?: string;
}

const Distribuidores = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productInputs, setProductInputs] = useState<Record<string, unknown> | null>(null);
  const [showInputs, setShowInputs] = useState(false);
  
  const handleShowInputsClose = () => {
    setShowInputs(false);
    setSelectedProduct(null);
    setProductInputs(null);
  };
  
  // Filter states
  const [potenciaMin, setPotenciaMin] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Record<number, string>>({});
  
  const generateToken = async () => {
    setLoading(true);
    console.log('🔄 Iniciando geração de token...');
    try {
      // Usar getApiPath para converter URL relativa para absoluta se necessário
      const { getApiPath } = await import('@/lib/api');
      const apiUrl = getApiPath('/api/aldo/token-python');
      console.log('📡 Fazendo requisição para:', apiUrl);
      
      // Usar XMLHttpRequest diretamente para evitar monkey-patch do fetch (exceção para Aldo)
      const response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', apiUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          const response = new Response(xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: new Headers()
          });
          resolve(response);
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify({}));
      });
      
      console.log('📡 Resposta recebida:', response.status, response.statusText);
      const data = await response.json();
      console.log('📡 Dados recebidos:', data);
      
      if (data.Data && data.Data.Token) {
        setToken(data.Data.Token);
        console.log('✅ Token definido com sucesso');
        toast({ title: 'Sucesso', description: 'Token gerado com sucesso' });
      } else {
        console.error('❌ Resposta não contém token válido:', data);
        throw new Error('Falha ao gerar token');
      }
    } catch (error) {
      console.error('❌ Erro ao gerar token:', error);
      toast({ title: 'Erro', description: 'Falha ao gerar token: ' + (error as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const buildFiltroAtributos = () => {
    const filters = [];
    
    // Add selected filters
    Object.entries(selectedFilters).forEach(([filterId, value]) => {
        if (value && value !== 'all') {
            filters.push(`${filterId}:${value}`);
        }
    });
    
    // Add hidden filters with predefined values
    // Bateria: always "Todos" (don't add to filters)
    // Bomba da agua solar: always "Todos" (don't add to filters)
    // Tipo Produto: always "Gerador" (ID 23)
    filters.push(`1:23`);
    
    // Add power filter with maximum value (3000) if only minimum is provided
    // Note: According to Aldo API documentation, power limit is 0 to 3000 kWp
    if (potenciaMin) {
      filters.push(`3:${potenciaMin}~3000`);
    }
    
    return filters.length > 0 ? filters.join(';') : null;
  };
  
  const fetchKits = async () => {
    if (!token) {
      toast({ title: 'Erro', description: 'Gere o token primeiro', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const filtroAtributos = buildFiltroAtributos();
      
      const body = {
        slug: 'energia-solar/gerador-de-energia-solar-fotovoltaico',
        origem: 'categoria',
        filtroAtributos: filtroAtributos,
        offset: 0,
        filterId: 0,
        orderby: 1 // Ordenar por menor preço
      };
      
      const response = await fetch('/api/aldo/products-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, body }),
      });
      
      const data = await response.json();
      
      if (data.Produtos) {
        // Sort products by price in ascending order
        const sortedProducts = [...data.Produtos].sort((a, b) => a.Preco - b.Preco);
        setProducts(sortedProducts);
        toast({ title: 'Sucesso', description: `Produtos carregados: ${sortedProducts.length} itens` });
      } else {
        throw new Error(data.error || 'Falha ao carregar produtos');
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast({ title: 'Erro', description: 'Falha ao carregar produtos: ' + (error as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProductInputs = async (product: Product) => {
    if (!token) {
      toast({ title: 'Erro', description: 'Gere o token primeiro', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch('/api/aldo/products-inputs-python', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, codigoProduto: product.Codigo.toString() }),
      });
      
      const data = await response.json();
      
      if (data && !data.error) {
        setSelectedProduct(product);
        setProductInputs(data);
        setShowInputs(true);
        toast({ title: 'Sucesso', description: 'Insumos carregados com sucesso' });
      } else {
        throw new Error(data.error || 'Falha ao carregar insumos');
      }
    } catch (error) {
      console.error('Erro ao carregar insumos:', error);
      toast({ title: 'Erro', description: 'Falha ao carregar insumos: ' + (error as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (filterId: number, value: string) => {
    setSelectedFilters(prev => {
      if (value === 'all' || value === '') {
        // Remove the filter if "all" is selected or if value is empty
        const { [filterId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [filterId]: value
      };
    });
  };
  
  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">Distribuidores</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Consulta de Kits Aldo Solar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
          {!token ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-lg mb-4">Para consultar os kits, é necessário gerar um token de acesso.</p>
              <Button onClick={generateToken} disabled={loading}>
                {loading ? 'Gerando token...' : 'Gerar Token'}
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Filtros para Consulta de Kits</h3>
                
                {/* Power filter */}
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="potenciaMin">Potência Mínima (kWp)</Label>
                    <Input
                      id="potenciaMin"
                      type="number"
                      step="0.1"
                      value={potenciaMin}
                      onChange={(e) => setPotenciaMin(e.target.value)}
                      placeholder="Ex: 3.0"
                    />
                  </div>
                </div>
                
                {/* Other filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtersData.Dados.filter(f => f.Id !== 3 && f.Id !== 13 && f.Id !== 208 && f.Id !== 1 && f.Id !== 6 && f.Id !== 8).map((filter) => (
                    <div key={filter.Id}>
                      <Label htmlFor={`filter-${filter.Id}`}>{filter.Descricao}</Label>
                      {filter.Valores ? (
                        <Select
                          value={selectedFilters[filter.Id] || 'all'}
                          onValueChange={(value) => handleFilterChange(filter.Id, value)}
                        >
                          <SelectTrigger id={`filter-${filter.Id}`}>
                            <SelectValue placeholder={`Selecione ${filter.Descricao}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {filter.Valores.map((valor) => (
                              <SelectItem key={valor.Id} value={valor.Id.toString()}>
                                {valor.Descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <Button onClick={fetchKits} disabled={loading} className="w-full sm:w-auto">
                    {loading ? 'Buscando...' : 'Buscar Kits'}
                  </Button>
                  <Button variant="outline" onClick={generateToken} disabled={loading} className="w-full sm:w-auto">
                    Regenerar Token
                  </Button>
                </div>
              </div>
              
              {products.length > 0 && (
                <div className="mt-4 md:mt-6">
                  <h3 className="text-lg font-semibold mb-3 md:mb-4">Resultados ({products.length} itens)</h3>
                  {isMobile ? (
                    // Mobile: Cards layout
                    <div className="space-y-3">
                      {products.map((product) => (
                        <Card key={product.Codigo} className="border">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1">ID: {product.Codigo}</div>
                                <h4 className="font-semibold text-sm leading-tight line-clamp-2">{product.Nome}</h4>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">Preço</div>
                                <div className="font-semibold text-base text-green-600">R$ {product.Preco?.toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">Potência</div>
                                <div className="font-medium">{product.Potencia?.toFixed(2)} kWp</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">R$/Wp</div>
                                <div className="font-medium text-sm">R$ {(product.Preco / (product.Potencia * 1000)).toFixed(4)}</div>
                              </div>
                              <div className="flex items-end">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => fetchProductInputs(product)}
                                  className="w-full"
                                >
                                  Ver Insumos
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    // Desktop: Table layout
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID Produto</TableHead>
                            <TableHead>Descrição do Kit</TableHead>
                            <TableHead>Preço</TableHead>
                            <TableHead>Potência (kWp)</TableHead>
                            <TableHead>R$/Wp</TableHead>
                            <TableHead>Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {products.map((product) => (
                            <TableRow key={product.Codigo}>
                              <TableCell>{product.Codigo}</TableCell>
                              <TableCell>{product.Nome}</TableCell>
                              <TableCell>R$ {product.Preco?.toFixed(2)}</TableCell>
                              <TableCell>{product.Potencia?.toFixed(2)}</TableCell>
                              <TableCell>R$ {(product.Preco / (product.Potencia * 1000)).toFixed(4)}</TableCell>
                              <TableCell>
                                <Button variant="outline" size="sm" onClick={() => fetchProductInputs(product)}>Ver Insumos</Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={showInputs} onOpenChange={(open) => {
        if (!open) {
          handleShowInputsClose();
        } else {
          setShowInputs(true);
        }
      }}>
        <DialogContent className="max-w-[95vw] md:max-w-3xl max-h-[90vh] md:max-h-[80vh] p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg pr-6">
              Insumos do Produto: {selectedProduct?.Codigo} - {selectedProduct?.Nome}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] md:h-[60vh]">
            {productInputs ? (
              <div className="space-y-4 p-2 md:p-4">
                {/* Check if we have an Insumos array */}
                {productInputs.Insumos && Array.isArray(productInputs.Insumos) ? (
                  <div className="space-y-3">
                    {productInputs.Insumos.map((input: any, index: number) => (
                      <div key={index} className="border rounded-lg p-4 bg-white">
                        <h4 className="font-semibold text-lg mb-2">
                          {input.Descricao || input.descricao || 'Produto sem descrição'}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Quantidade:</span> {input.Quantidade || input.quantidade || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Valor:</span> R$ {typeof input.Valor === 'number' ? input.Valor.toFixed(2) : (typeof input.valor === 'number' ? input.valor.toFixed(2) : 'N/A')}
                          </div>
                          <div>
                            <span className="font-medium">Tipo:</span> {input.Tipo || input.tipo || 'N/A'}
                          </div>
                          {/* Special fields for inversor */}
                          {(input.Tipo?.toLowerCase().includes('inversor') || input.tipo?.toLowerCase().includes('inversor')) && (
                            <>
                              {input.LinkFolheto && (
                                <div>
                                  <span className="font-medium">Link do Folheto:</span>{' '}
                                  <a href={input.LinkFolheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Abrir folheto
                                  </a>
                                </div>
                              )}
                              {input.Imagem && (
                                <div>
                                  <span className="font-medium">Link da Imagem:</span>{' '}
                                  <a href={input.Imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Ver imagem
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                          {/* Special field for painel */}
                          {(input.Tipo?.toLowerCase().includes('painel') || input.tipo?.toLowerCase().includes('painel')) && (
                            <div>
                              <span className="font-medium">Potência:</span> {input.Potencia ? `${input.Potencia} W` : (input.potencia ? `${input.potencia} W` : 'N/A')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(productInputs.Data) ? (
                  <div className="space-y-3">
                    {productInputs.Data.map((input: any, index: number) => (
                      <div key={index} className="border rounded-lg p-4 bg-white">
                        <h4 className="font-semibold text-lg mb-2">
                          {input.descricao || input.Descricao || input.descricao_produto || input.Descricao_Produto || 'Produto sem descrição'}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Quantidade:</span> {input.quantidade || input.Quantidade || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Valor:</span> R$ {typeof input.valor === 'number' ? input.valor.toFixed(2) : (input.Valor ? input.Valor : 'N/A')}
                          </div>
                          <div>
                            <span className="font-medium">Tipo:</span> {input.tipo || input.Tipo || 'N/A'}
                          </div>
                          {/* Special fields for inversor */}
                          {input.tipo && (input.tipo.toLowerCase().includes('inversor') || input.Tipo?.toLowerCase().includes('inversor')) && (
                            <>
                              {input.link_folheto && (
                                <div>
                                  <span className="font-medium">Link do Folheto:</span>{' '}
                                  <a href={input.link_folheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Abrir folheto
                                  </a>
                                </div>
                              )}
                              {input.imagem && (
                                <div>
                                  <span className="font-medium">Link da Imagem:</span>{' '}
                                  <a href={input.imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Ver imagem
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                          {/* Special field for painel */}
                          {input.tipo && (input.tipo.toLowerCase().includes('painel') || input.Tipo?.toLowerCase().includes('painel')) && (
                            <div>
                              <span className="font-medium">Potência:</span> {input.potencia ? `${input.potencia} W` : (input.Potencia ? `${input.Potencia} W` : 'N/A')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : typeof productInputs.Data === 'object' && productInputs.Data !== null ? (
                  <div className="space-y-3">
                    {/* When productInputs.Data is an object, we display it as a single insumo */}
                    <div className="border rounded-lg p-4 bg-white">
                      <h4 className="font-semibold text-lg mb-2">
                        {(productInputs.Data as any).descricao || (productInputs.Data as any).Descricao || (productInputs.Data as any).descricao_produto || (productInputs.Data as any).Descricao_Produto || 'Produto sem descrição'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="font-medium">Quantidade:</span> {(productInputs.Data as any).quantidade || (productInputs.Data as any).Quantidade || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Valor:</span> R$ {typeof (productInputs.Data as any).valor === 'number' ? (productInputs.Data as any).valor.toFixed(2) : ((productInputs.Data as any).Valor ? (productInputs.Data as any).Valor : 'N/A')}
                        </div>
                        <div>
                          <span className="font-medium">Tipo:</span> {(productInputs.Data as any).tipo || (productInputs.Data as any).Tipo || 'N/A'}
                        </div>
                        {/* Special fields for inversor */}
                        {(productInputs.Data as any).tipo && ((productInputs.Data as any).tipo.toLowerCase().includes('inversor') || (productInputs.Data as any).Tipo?.toLowerCase().includes('inversor')) && (
                          <>
                            {(productInputs.Data as any).link_folheto && (
                              <div>
                                <span className="font-medium">Link do Folheto:</span>{' '}
                                <a href={(productInputs.Data as any).link_folheto} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  Abrir folheto
                                </a>
                              </div>
                            )}
                            {(productInputs.Data as any).imagem && (
                              <div>
                                <span className="font-medium">Link da Imagem:</span>{' '}
                                <a href={(productInputs.Data as any).imagem} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  Ver imagem
                                </a>
                              </div>
                            )}
                          </>
                        )}
                        {/* Special field for painel */}
                        {(productInputs.Data as any).tipo && ((productInputs.Data as any).tipo.toLowerCase().includes('painel') || (productInputs.Data as any).Tipo?.toLowerCase().includes('painel')) && (
                          <div>
                            <span className="font-medium">Potência:</span> {(productInputs.Data as any).potencia ? `${(productInputs.Data as any).potencia} W` : ((productInputs.Data as any).Potencia ? `${(productInputs.Data as any).Potencia} W` : 'N/A')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 bg-white">
                    <h4 className="font-semibold text-lg mb-2">Informações do Produto</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Descrição:</span> {String(productInputs)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="p-4">Carregando insumos...</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Distribuidores;
